import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

import { errorToastMessage } from '../components/layout/toast-root/toast';
import { LoginUserStatus } from '../context/AuthContext';
import { isFetchAbortSerializedError } from '../redux/generic';
import { markStartupComplete } from '../utils/startup/startup-perf-logger';

const SPLASH_SAFETY_TIMEOUT_MS = 10_000;

let nativeSplashHiddenThisJsSession = false;

let startupSplashErrorToastShown = false;

function tryStartupSplashErrorToast(params: {
  versionError: unknown;
  loginUserStatus: LoginUserStatus;
  t: (key: string) => string;
}): void {
  if (startupSplashErrorToastShown) return;

  const { versionError, loginUserStatus, t } = params;

  const versionFailed =
    !!versionError &&
    typeof versionError === 'object' &&
    !isFetchAbortSerializedError(
      versionError as {
        status?: string | number;
        error?: unknown;
        data?: unknown;
      },
    );

  const authFailed = loginUserStatus === LoginUserStatus.error;

  if (!versionFailed && !authFailed) return;

  startupSplashErrorToastShown = true;
  errorToastMessage(t('error.somethingWentWrong'));
}

function completeSplashHide(params: {
  loginUserStatus: LoginUserStatus;
  versionError: unknown;
  t: (key: string) => string;
}): void {
  if (nativeSplashHiddenThisJsSession) {
    return;
  }

  nativeSplashHiddenThisJsSession = true;
  markStartupComplete({
    platform: Capacitor.getPlatform(),
    loginUserStatus: params.loginUserStatus,
  });
  tryStartupSplashErrorToast({
    versionError: params.versionError,
    loginUserStatus: params.loginUserStatus,
    t: params.t,
  });
}

function hideNativeSplash(params: {
  loginUserStatus: LoginUserStatus;
  versionError: unknown;
  t: (key: string) => string;
}): (() => void) | void {
  if (nativeSplashHiddenThisJsSession) {
    return;
  }

  if (Capacitor.getPlatform() === 'web') {
    completeSplashHide({
      loginUserStatus: params.loginUserStatus,
      versionError: params.versionError,
      t: params.t,
    });
    return;
  }

  let innerFrame = 0;
  const outerFrame = requestAnimationFrame(() => {
    innerFrame = requestAnimationFrame(() => {
      void SplashScreen.hide({ fadeOutDuration: 300 })
        .then(() => {
          completeSplashHide({
            loginUserStatus: params.loginUserStatus,
            versionError: params.versionError,
            t: params.t,
          });
        })
        .catch(() => {
          completeSplashHide({
            loginUserStatus: params.loginUserStatus,
            versionError: params.versionError,
            t: params.t,
          });
        });
    });
  });

  return () => {
    cancelAnimationFrame(outerFrame);
    if (innerFrame) {
      cancelAnimationFrame(innerFrame);
    }
  };
}

export function useHideNativeSplashOnStartupReady(params: {
  isLoadingVersion: boolean;
  loginUserStatus: LoginUserStatus;
  versionError: unknown;
}): void {
  const { t } = useTranslation();
  const { isLoadingVersion, loginUserStatus, versionError } = params;
  const latestParamsRef = useRef(params);
  latestParamsRef.current = params;

  const isStartupGateResolved =
    !isLoadingVersion && loginUserStatus !== LoginUserStatus.loading;

  useEffect(() => {
    const safetyTimer = setTimeout(() => {
      if (nativeSplashHiddenThisJsSession) {
        return;
      }
      hideNativeSplash({
        loginUserStatus: latestParamsRef.current.loginUserStatus,
        versionError: latestParamsRef.current.versionError,
        t,
      });
    }, SPLASH_SAFETY_TIMEOUT_MS);

    return () => {
      clearTimeout(safetyTimer);
    };
  }, [t]);

  useEffect(() => {
    if (!isStartupGateResolved) return;
    if (nativeSplashHiddenThisJsSession) return;

    return (
      hideNativeSplash({
        loginUserStatus,
        versionError,
        t,
      }) ?? undefined
    );
  }, [isStartupGateResolved, versionError, loginUserStatus, t]);
}
